import React, { useEffect, useRef, useState } from "react";
import { Home } from "lucide-react";
import { UI, DS } from "../designSystem";

// Tela de leitura de e-book (visualizador de PDF + anotações + "ler junto").
// Extraída de PrototypeApp.js — antes vivia inline como um bloco
// `if (screen === "ebook")` dentro do componente principal.
//
// Observação importante: `ebookCanvasRef` é o MESMO objeto de ref criado no
// componente pai (useRef), passado aqui como prop comum. O efeito que
// desenha o PDF no canvas (pdf.js) continua no componente pai e lê
// `ebookCanvasRef.current` — como é o mesmo objeto, ele enxerga o elemento
// <canvas> renderizado aqui normalmente, sem precisar de forwardRef.
export default function EbookScreen({
  selectedEbook,
  ebookNotes,
  setEbookNotes,
  ebookTab,
  setEbookTab,
  ebookPage,
  setEbookPage,
  ebookPageLoading,
  ebookPageError,
  ebookCanvasRef,
  persistEbookNote,
  syncEbookState,
  setScreen,
  setShowInvitePicker,
  renderBottomNav,
  isMobile,
  isTablet,
  isDesktop,
  styles,
  ebookZoomLevel,
  onIncreaseEbookZoom,
  onDecreaseEbookZoom,
  ebookZoomMin,
  ebookZoomMax
}) {
  const {
    containerStyle,
    fixedTopBarStyle,
    buttonReset,
    pageContentStyle,
    h1Style,
    overflowGuardStyle
  } = styles;

  // Largura do visor do PDF pro zoom (A-/A+) -- calculada, NAO medida no
  // proprio elemento. Motivo: a coluna de leitura (article) ja alarga
  // sozinha quando o zoom esta ativo (ver maxWidth do article abaixo),
  // entao medir o visor DEPOIS que ele ja alargou e multiplicar pelo zoom
  // de novo em cima disso faz a conta se somar duas vezes (largura ja
  // alargada x zoom) -- sempre estourava o proprio espaco que tinha
  // acabado de ganhar, nao importa quanto se alargasse a coluna. Por isso
  // a conta aqui usa so' a largura real da janela (window.innerWidth) e os
  // mesmos numeros fixos de padding/maxWidth usados no resto da tela,
  // nunca o tamanho JA renderizado do proprio visor.
  const ebookViewerBoxRef = useRef(null);
  const [ebookWindowWidth, setEbookWindowWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 0));
  useEffect(() => {
    const handleResize = () => setEbookWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Mesmos numeros do pageContentStyle (PrototypeApp.js), do section e do
  // visor logo abaixo -- se um desses mudar, atualizar aqui tambem.
  // No celular, em 100% a pagina ja' usa toda a largura disponivel -- ao
  // contrario de desktop/tablet (EBOOK_PAGE_MAX_WIDTH generoso, sobra
  // espaco quando a coluna alarga), nao ha' "vazio" pra pagina crescer
  // DENTRO da tela sem passar da borda. Por isso, so' no celular, o zoom
  // nao alarga a pagina pra alem da tela (que e' o que obrigava a
  // arrastar pro lado) -- ele encolhe as margens/respiro ao redor dela,
  // liberando espaco real pro conteudo crescer. Ganha bem menos tamanho
  // que em desktop/tablet, mas a largura calculada abaixo nunca passa da
  // janela por construcao (nao por um teto aplicado depois) -- pedido da
  // Carla, 26/10/2026: "nao eh legal ficar arrastando pro lado".
  const ebookMobileZoomFraction = (() => {
    if (!isMobile) return 0;
    const min = ebookZoomMin || 1;
    const max = ebookZoomMax || min;
    if (max <= min) return 0;
    return Math.max(0, Math.min(1, (ebookZoomLevel - min) / (max - min)));
  })();
  const ebookMobileOuterPadding = 10 - ebookMobileZoomFraction * 6; // 10 -> 4
  const ebookMobileSectionPadding = 10 - ebookMobileZoomFraction * 6; // 10 -> 4
  const ebookMobileViewerPadding = 8 - ebookMobileZoomFraction * 4; // 8 -> 4
  const EBOOK_PAGE_OUTER_PADDING = isDesktop ? 24 : isTablet ? 16 : ebookMobileOuterPadding;
  const EBOOK_PAGE_MAX_WIDTH = isDesktop ? 1180 : isTablet ? 760 : Infinity;
  const EBOOK_SECTION_PADDING = isTablet ? 18 : ebookMobileSectionPadding;
  const EBOOK_VIEWER_PADDING = isTablet ? 18 : ebookMobileViewerPadding;
  const ebookPageAvailableWidth = Math.min(EBOOK_PAGE_MAX_WIDTH, ebookWindowWidth) - EBOOK_PAGE_OUTER_PADDING * 2;
  // Largura "normal" (sem zoom) da pagina, igual a coluna estreita de
  // leitura de sempre (720, ou a tela toda se for menor que isso). No
  // celular ja' inclui o encolhimento de margem do zoom (ver comentario
  // acima) -- por isso NAO e' multiplicada de novo pelo zoom mais abaixo
  // (ebookCanvasWidth), diferente de desktop/tablet.
  const ebookNaturalCanvasWidth = Math.max(0, Math.min(720, ebookPageAvailableWidth) - EBOOK_SECTION_PADDING * 2 - EBOOK_VIEWER_PADDING * 2);
  const ebookViewerBaseWidth = ebookNaturalCanvasWidth > 0 ? ebookNaturalCanvasWidth : null;
  // Teto de verdade (coluna toda alargada) -- usado so' pra garantir que a
  // caixa, mesmo acompanhando o tamanho da pagina, nunca passe do espaco
  // real disponivel.
  const ebookWidenedAvailableWidth = Math.max(0, ebookPageAvailableWidth - EBOOK_SECTION_PADDING * 2);
  // Largura de fato usada pela pagina/canvas com zoom: no celular o
  // crescimento ja' veio do encolhimento de margem (ebookViewerBaseWidth
  // acima), entao e' so' ele mesmo; em desktop/tablet continua sendo o
  // calculo de sempre (base vezes o zoom).
  const ebookCanvasWidth = ebookViewerBaseWidth
    ? (isMobile ? ebookViewerBaseWidth : Math.round(ebookViewerBaseWidth * (ebookZoomLevel || 1)))
    : null;

  // Arrastar-pra-rolar (clique e arraste) no visor do PDF com zoom ativo.
  // A barra de rolagem normal as vezes nao e' suficiente -- no trackpad do
  // Mac ela fica escondida ate' tocar, e mesmo com a dica/estilo forcando
  // ela visivel, continuava parecendo "cortado". Arrastar com o mouse e'
  // mais a prova de falhas: o conteudo visivelmente se move junto com o
  // cursor, sem depender de gesto de trackpad ou configuracao do sistema.
  const ebookDragState = useRef(null);
  const [isDraggingEbookPage, setIsDraggingEbookPage] = useState(false);
  const handleEbookViewerMouseDown = (event) => {
    const node = ebookViewerBoxRef.current;
    if (!node || (ebookZoomLevel || 1) <= 1 || isMobile) return;
    // So' arrasta com o botao principal, e nao quando o clique comeca em
    // cima de um link/botao dentro do visor (nao tem nenhum hoje, mas por
    // seguranca).
    if (event.button !== 0) return;
    ebookDragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: node.scrollLeft,
      startScrollTop: node.scrollTop
    };
    setIsDraggingEbookPage(true);
  };
  useEffect(() => {
    if (!isDraggingEbookPage) return;
    const node = ebookViewerBoxRef.current;
    const handleMove = (event) => {
      if (!ebookDragState.current || !node) return;
      const dx = event.clientX - ebookDragState.current.startX;
      const dy = event.clientY - ebookDragState.current.startY;
      node.scrollLeft = ebookDragState.current.startScrollLeft - dx;
      node.scrollTop = ebookDragState.current.startScrollTop - dy;
    };
    const handleUp = () => {
      ebookDragState.current = null;
      setIsDraggingEbookPage(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isDraggingEbookPage]);

  const activeEbookNote = ebookNotes[selectedEbook.id] || "";
  const ebookAccent = selectedEbook.coverColor || "#E6B15A";
  const totalEbookPages = Math.max(1, selectedEbook.pages || 1);
  const currentEbookPage = Math.min(Math.max(ebookPage, 1), totalEbookPages);
  const ebookProgressPercent = Math.round((currentEbookPage / totalEbookPages) * 100);
  const readingTabs = [
    ["reading", "Leitura"],
    ["note", "Anotacao"],
    ["together", "Juntos"]
  ];

  return (
    <div style={containerStyle}>
      {/* Forca a barra de rolagem do visor do PDF a ficar sempre visivel --
          no Mac/trackpad ela some por padrao ate' voce' tocar nela, o que
          fazia a pagina com zoom parecer cortada de vez em vez de rolavel. */}
      <style>{`
        .ebook-zoom-viewer::-webkit-scrollbar { height: 10px; }
        .ebook-zoom-viewer::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
        .ebook-zoom-viewer::-webkit-scrollbar-thumb { background: rgba(230,177,90,0.55); border-radius: 999px; }
        .ebook-zoom-viewer { scrollbar-width: auto; scrollbar-color: rgba(230,177,90,0.55) rgba(255,255,255,0.05); }
      `}</style>
      <div style={{ ...fixedTopBarStyle, borderBottom: `2px solid ${selectedEbook.coverColor}` }}>
        <button onClick={() => setScreen("main")} style={{ ...buttonReset, fontSize: 24, color: "#9CA3AF", marginRight: 20 }}>←</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: 1.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedEbook.title}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#c7d1e4", fontWeight: 800, letterSpacing: 1 }}>{currentEbookPage}/{totalEbookPages}</span>
        <a href="/" aria-label="Voltar para o site institucional" title="Início do site" style={{ marginLeft: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 999, color: "#9CA3AF", textDecoration: "none", flexShrink: 0 }}>
          <Home size={18} />
        </a>
      </div>

      <div style={pageContentStyle}>
        {/* Com zoom ativo (>100%), a coluna de leitura "solta" o teto
            estreito de 720px (pensado pra leitura confortavel sem zoom) e
            usa o mesmo teto largo da pagina (pageContentStyle) -- assim a
            pagina ampliada tem espaco de verdade antes de precisar rolar
            pro lado, em vez de depender so' de rolagem horizontal escondida
            (no Mac a barra de rolagem fica invisivel ate' voce' interagir,
            o que parecia "cortado" sem ser). */}
        <article style={{ maxWidth: ebookTab === "reading" && ebookZoomLevel > 1 ? (isDesktop ? 1180 : isTablet ? 760 : "100%") : 720, width: "100%", boxSizing: "border-box", margin: "0 auto", position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "72px minmax(0, 1fr)" : isTablet ? "112px minmax(0, 1fr)" : "84px minmax(0, 1fr)", gap: isTablet ? 22 : 14, alignItems: "end", marginBottom: 26 }}>
            <img src={selectedEbook.coverImage} alt="" style={{ width: isMobile ? 72 : isTablet ? 112 : 84, height: isMobile ? 104 : isTablet ? 158 : 118, objectFit: "cover", objectPosition: "top center", boxShadow: "0 18px 30px rgba(0,0,0,0.42)" }} />
            <div>
              <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 2, color: ebookAccent, textTransform: "uppercase", fontWeight: 900 }}>Plano de leitura</p>
              <h1 style={{ ...h1Style, margin: 0, fontSize: isDesktop ? 42 : isTablet ? 34 : 27, lineHeight: 1.04, color: "#ffffff", fontWeight: 900 }}>{selectedEbook.title}</h1>
              <p style={{ margin: "10px 0 16px", fontSize: 14, lineHeight: 1.55, color: "#aeb9ca" }}>{selectedEbook.subtitle}</p>
              <div style={{ maxWidth: 360 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                  <span style={{ fontSize: 10, color: "#7f8da3", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 900 }}>Progresso</span>
                  <span style={{ fontSize: 10, color: ebookAccent, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 900 }}>{ebookProgressPercent}%</span>
                </div>
                <div style={{ height: 10, background: "rgba(255,255,255,0.09)", padding: 2 }}>
                  <div style={{ width: `${ebookProgressPercent}%`, height: "100%", background: ebookAccent, transition: "width 0.25s ease" }} />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 30, borderBottom: "2px solid rgba(255,255,255,0.08)", overflowX: "auto" }}>
            {readingTabs.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setEbookTab(key)}
                style={{ ...buttonReset, minHeight: 44, padding: "0 14px", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap", color: ebookTab === key ? "#ffffff" : "#718096", borderBottom: ebookTab === key ? `3px solid ${ebookAccent}` : "3px solid transparent", marginBottom: -2 }}
              >
                {label}
              </button>
            ))}
          </div>

          {ebookTab === "reading" && (
            <section style={{ ...overflowGuardStyle, background: "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))", border: `1px solid rgba(255,255,255,0.12)`, borderTop: `4px solid ${ebookAccent}`, padding: isTablet ? 18 : 10, boxShadow: "0 20px 38px rgba(0,0,0,0.28)" }}>
              {/* Controle de tamanho da letra (A-/A+) -- pedido da Carla,
                  25/10/2026: "letra esta pequena e o pessoal nao consegue
                  aumentar e diminuir". A pagina e' um PDF desenhado num
                  canvas (pdf.js), entao "aumentar letra" aqui re-renderiza
                  a pagina numa escala maior (ver ebookZoomLevel em
                  PrototypeApp.js), nao muda CSS de texto de verdade. */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={onDecreaseEbookZoom}
                  disabled={ebookZoomLevel <= ebookZoomMin}
                  aria-label="Diminuir letra"
                  style={{ ...buttonReset, minWidth: 44, minHeight: 40, borderRadius: 4, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)", color: ebookZoomLevel <= ebookZoomMin ? "#526071" : "#ffffff", fontSize: 13, fontWeight: 900, cursor: ebookZoomLevel <= ebookZoomMin ? "not-allowed" : "pointer" }}
                >
                  A−
                </button>
                <span style={{ minWidth: 50, textAlign: "center", fontSize: 11, color: "#aeb9ca", fontWeight: 900 }}>{Math.round(ebookZoomLevel * 100)}%</span>
                <button
                  type="button"
                  onClick={onIncreaseEbookZoom}
                  disabled={ebookZoomLevel >= ebookZoomMax}
                  aria-label="Aumentar letra"
                  style={{ ...buttonReset, minWidth: 44, minHeight: 40, borderRadius: 4, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)", color: ebookZoomLevel >= ebookZoomMax ? "#526071" : "#ffffff", fontSize: 15, fontWeight: 900, cursor: ebookZoomLevel >= ebookZoomMax ? "not-allowed" : "pointer" }}
                >
                  A+
                </button>
              </div>
              {ebookZoomLevel > 1 && !isMobile && (
                // Dica de que da' pra arrastar pro lado -- no Mac a barra de
                // rolagem fica invisivel ate' voce' tocar nela, entao sem
                // isso parece que a pagina ficou cortada de vez. No celular
                // nunca aparece: o zoom la' encolhe margem em vez de
                // alargar a pagina, entao nunca sobra nada fora da caixa
                // pra arrastar (ver EBOOK_PAGE_OUTER_PADDING acima).
                <p style={{ margin: "0 0 8px", textAlign: "center", fontSize: 10.5, color: "#8fa0bf", fontWeight: 800 }}>✋ clique e arraste a página pra ver o resto da linha</p>
              )}
              <div
                ref={ebookViewerBoxRef}
                className="ebook-zoom-viewer"
                onMouseDown={handleEbookViewerMouseDown}
                style={{
                  // Com zoom ativo, a caixa escura acompanha o tamanho real
                  // da pagina (em vez de sempre esticar pra coluna larga
                  // inteira) -- senao sobrava uma barra preta vazia do lado
                  // quando a pagina nao precisava de todo aquele espaco.
                  width: ebookZoomLevel > 1 && ebookCanvasWidth
                    ? Math.min(ebookWidenedAvailableWidth, ebookCanvasWidth + EBOOK_VIEWER_PADDING * 2)
                    : "100%",
                  maxWidth: "100%",
                  margin: ebookZoomLevel > 1 ? "0 auto" : undefined,
                  background: "#101624",
                  border: "1px solid rgba(255,255,255,0.12)",
                  minHeight: isDesktop ? 720 : isTablet ? 560 : 430,
                  overflow: "auto",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: ebookZoomLevel > 1 ? "flex-start" : "center",
                  padding: EBOOK_VIEWER_PADDING,
                  position: "relative",
                  cursor: ebookZoomLevel > 1 && !isMobile ? (isDraggingEbookPage ? "grabbing" : "grab") : "default",
                  userSelect: isDraggingEbookPage ? "none" : "auto"
                }}
              >
                {ebookPageLoading && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(16,22,36,0.72)", color: "#d7dce5", fontSize: 12, fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase" }}>
                    Carregando página
                  </div>
                )}
                {ebookPageError && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: "#fca5a5", fontSize: 13, lineHeight: 1.5 }}>
                    {ebookPageError}
                  </div>
                )}
                <canvas
                  ref={ebookCanvasRef}
                  aria-label={`${selectedEbook.title} página ${currentEbookPage}`}
                  style={
                    ebookCanvasWidth
                      ? {
                          // Largura em px de verdade (nao %) -- ver
                          // ebookCanvasWidth mais acima (base vezes zoom
                          // em desktop/tablet; so' a base, ja' maior por
                          // causa da margem encolhida, no celular). Quando
                          // passa do espaco disponivel (so' pode acontecer
                          // em desktop/tablet), o overflow:auto do visor
                          // vira scroll horizontal de verdade (mais
                          // arrastar com o mouse).
                          width: ebookCanvasWidth,
                          maxWidth: "none",
                          height: "auto",
                          display: "block",
                          flexShrink: 0,
                          background: "#ffffff",
                          boxShadow: "0 18px 34px rgba(0,0,0,0.36)"
                        }
                      : {
                          // So' cai aqui se a largura calculada der 0 ou
                          // menos (nao deveria acontecer) -- mesmo visual
                          // de sempre, sem zoom.
                          width: "100%",
                          maxWidth: 760,
                          height: "auto",
                          display: "block",
                          background: "#ffffff",
                          boxShadow: "0 18px 34px rgba(0,0,0,0.36)"
                        }
                  }
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto minmax(0, 1fr)", alignItems: "center", gap: isMobile ? 8 : 12, marginTop: 14 }}>
                <button
                  onClick={() => setEbookPage((page) => Math.max(1, page - 1))}
                  disabled={currentEbookPage <= 1}
                  style={{ ...buttonReset, minHeight: 46, padding: "0 14px", borderRadius: 4, border: `1px solid ${currentEbookPage <= 1 ? "rgba(255,255,255,0.08)" : ebookAccent}`, background: currentEbookPage <= 1 ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.08)", color: currentEbookPage <= 1 ? "#526071" : "#ffffff", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, cursor: currentEbookPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Pagina anterior
                </button>
                <span style={{ fontSize: 12, color: "#aeb9ca", fontWeight: 900, whiteSpace: "nowrap", textAlign: "center", order: isMobile ? -1 : 0 }}>{currentEbookPage} de {totalEbookPages}</span>
                <button
                  onClick={() => setEbookPage((page) => Math.min(totalEbookPages, page + 1))}
                  disabled={currentEbookPage >= totalEbookPages}
                  style={{ ...buttonReset, minHeight: 46, padding: "0 14px", borderRadius: 4, border: `1px solid ${currentEbookPage >= totalEbookPages ? "rgba(255,255,255,0.08)" : ebookAccent}`, background: currentEbookPage >= totalEbookPages ? "rgba(255,255,255,0.04)" : ebookAccent, color: currentEbookPage >= totalEbookPages ? "#526071" : "#111827", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, cursor: currentEbookPage >= totalEbookPages ? "not-allowed" : "pointer" }}
                >
                  Próxima página
                </button>
              </div>
            </section>
          )}

          {ebookTab === "note" && (
            <section>
              <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 2.2, color: "#8fa0bf", textTransform: "uppercase", fontWeight: 900 }}>Seu registro</p>
              <p style={{ margin: "0 0 18px", fontSize: 13, lineHeight: 1.55, color: "#718096" }}>Guarde aqui o que precisa virar memória, oração ou decisão.</p>
              <textarea
                value={activeEbookNote}
                onChange={(event) => {
                  const value = event.target.value;
                  setEbookNotes((prev) => ({ ...prev, [selectedEbook.id]: value }));
                  persistEbookNote(selectedEbook.id, value);
                  syncEbookState(selectedEbook.id, { note: value });
                }}
                placeholder="Escreva..."
                style={{ width: "100%", minHeight: 230, padding: 22, background: "#18181B", border: `1px solid ${UI.stroke}`, borderRadius: 4, color: "#FFFFFF", fontSize: 17, lineHeight: 1.7, fontFamily: DS.type.family, resize: "vertical", outline: "none", boxSizing: "border-box" }}
              />
            </section>
          )}

          {ebookTab === "together" && (
            <section style={{ padding: isTablet ? "32px 28px" : "26px 20px", background: "#111827", border: `1px solid ${UI.stroke}`, borderLeft: `4px solid ${ebookAccent}` }}>
              <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 2.2, color: ebookAccent, textTransform: "uppercase", fontWeight: 900 }}>Ler junto</p>
              <h2 style={{ margin: "0 0 12px", fontSize: 25, lineHeight: 1.15, color: "#ffffff", fontWeight: 900 }}>Uma leitura pode virar caminho compartilhado.</h2>
              <p style={{ margin: "0 0 20px", fontSize: 14, lineHeight: 1.65, color: "#aeb9ca" }}>Convide alguém para acompanhar este plano no mesmo ritmo, com espaço para conversa e registro.</p>
              <button onClick={() => setShowInvitePicker(true)} style={{ ...buttonReset, width: "100%", minHeight: 48, borderRadius: 4, background: ebookAccent, color: "#111827", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1 }}>
                Compartilhar leitura
              </button>
            </section>
          )}

        </article>
      </div>
      {renderBottomNav()}
    </div>
  );
}
